// src/App.jsx
import { useState } from 'react'
import { useAuth }     from './hooks/useAuth'
import { useProjects } from './hooks/useProjects'
import AuthPage        from './pages/AuthPage'

const TODAY = new Date().toISOString().split('T')[0]

const STATUS_CFG = {
  active:    { label:'Active',    color:'#00ff9d', bg:'rgba(0,255,157,0.10)',   dot:'#00ff9d' },
  pending:   { label:'Pending',   color:'#ffc107', bg:'rgba(255,193,7,0.10)',   dot:'#ffc107' },
  completed: { label:'Completed', color:'#4fc3f7', bg:'rgba(79,195,247,0.10)',  dot:'#4fc3f7' },
  paused:    { label:'Paused',    color:'#aaaaaa', bg:'rgba(170,170,170,0.10)', dot:'#aaaaaa' },
  failed:    { label:'Failed',    color:'#ff4d6d', bg:'rgba(255,77,109,0.10)',  dot:'#ff4d6d' },
}
const CHAINS   = ['Ethereum','Solana','BNB Chain','Arbitrum','Optimism','Base','zkSync','Starknet','Aptos','Sui','Other']
const CATS     = ['DeFi','NFT','GameFi','Layer 2','Infrastructure','DAO','Bridge','DEX','Lending','Social','Other']
const PRIOS    = [
  { value:'high',   label:'🔥 High',   color:'#ff4d6d' },
  { value:'medium', label:'⚡ Medium', color:'#ffc107' },
  { value:'low',    label:'💧 Low',    color:'#4fc3f7' },
]
const TASK_TPL = ['Connect Wallet','Follow Twitter','Join Discord','Join Telegram','Complete Testnet','Bridge Assets','Swap Tokens','Add Liquidity','Stake Tokens']
const CI_TPL   = ['Daily Login','Daily Swap','Daily Bridge','Daily Stake','Daily Vote','Claim Rewards','Daily Quest','Social Task','Check Website','Run Script']

const EMPTY = {
  id:null, name:'', chain:'Ethereum', category:'DeFi',
  status:'active', priority:'medium',
  estimatedReward:'', investedAmount:'', walletUsed:'',
  startDate:TODAY, deadline:'', website:'', twitter:'', discord:'',
  notes:'', tasks:[], tags:'', fundingAmount:'', backers:'',
  requiresDailyCheckin:false, checkinActions:[],
}

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2)

function streakOf(list = []) {
  if (!list.length) return { streak:0, todayDone:false, lastDate:null, total:0 }
  const sorted = [...list].sort((a,b) => b.date.localeCompare(a.date))
  const todayDone = sorted[0].date === TODAY
  let streak = 0, cur = new Date(TODAY)
  for (const c of sorted) {
    const diff = Math.round((cur - new Date(c.date)) / 86400000)
    if (diff === 0 || diff === 1) { streak++; cur = new Date(c.date) } else break
  }
  return { streak, todayDone, lastDate: sorted[0].date, total: list.length }
}

function last14Days() {
  return Array.from({ length:14 }, (_,i) => {
    const d = new Date(TODAY); d.setDate(d.getDate() - (13 - i))
    return d.toISOString().split('T')[0]
  })
}

const INP = {
  background:'#141920', border:'1px solid #1e2535', borderRadius:'8px',
  color:'#e2e8f0', padding:'9px 12px', fontSize:'13px', outline:'none',
  fontFamily:"'Inter',sans-serif", width:'100%', boxSizing:'border-box',
}
const BTN = (bg, border, color) => ({
  background:bg, border:`1px solid ${border}`, borderRadius:'8px',
  color, padding:'8px 16px', cursor:'pointer', fontSize:'12px',
  fontFamily:"'Space Mono',monospace", fontWeight:'700',
})

function Tag({ c='#00ff9d', children }) {
  return <span style={{ background:`${c}18`, color:c, border:`1px solid ${c}40`,
    borderRadius:'6px', padding:'2px 8px', fontSize:'11px',
    fontFamily:"'Space Mono',monospace", whiteSpace:'nowrap' }}>{children}</span>
}
function Lbl({ children }) {
  return <label style={{ color:'#8892b0', fontSize:'11px', fontFamily:"'Space Mono',monospace",
    textTransform:'uppercase', letterSpacing:'0.08em' }}>{children}</label>
}
function F({ label, span2, children }) {
  return <div style={{ display:'flex', flexDirection:'column', gap:'5px', gridColumn:span2?'span 2':'span 1' }}>
    <Lbl>{label}</Lbl>{children}
  </div>
}

function Modal({ open, onClose, title, wide, children }) {
  if (!open) return null
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:1000,
      background:'rgba(0,0,0,0.82)', backdropFilter:'blur(6px)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:'12px' }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:'#0e1117', border:'1px solid #1e2535', borderRadius:'16px',
        width:'100%', maxWidth:wide?'840px':'520px', maxHeight:'92vh', overflowY:'auto',
        boxShadow:'0 0 70px rgba(0,255,157,0.07)', padding:'20px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <h2 style={{ color:'#e2e8f0', fontSize:'15px', fontFamily:"'Space Mono',monospace", margin:0 }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#666', cursor:'pointer', fontSize:'22px' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function Heatmap({ list = [], days = 14 }) {
  const dates = last14Days().slice(-days)
  const done  = new Set(list.map(c => c.date))
  return (
    <div>
      <div style={{ display:'flex', gap:'3px', flexWrap:'wrap' }}>
        {dates.map(d => (
          <div key={d} title={d} style={{
            width:'14px', height:'14px', borderRadius:'3px',
            background: done.has(d) ? '#00ff9d' : '#1a2030',
            border: d===TODAY ? '1px solid #00ff9d80' : '1px solid transparent',
            opacity: done.has(d) ? 1 : 0.6,
          }}/>
        ))}
      </div>
      <div style={{ color:'#555', fontSize:'9px', fontFamily:"'Space Mono',monospace", marginTop:'3px' }}>14-day activity</div>
    </div>
  )
}

function SyncBadge({ syncing, error }) {
  if (error)   return <span style={{ color:'#ff4d6d', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>⚠ Error</span>
  if (syncing) return <span style={{ color:'#ffc107', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>⟳ Sync...</span>
  return <span style={{ color:'#00ff9d', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>✓ Synced</span>
}

function CheckinModal({ project, checkins, onCheckin, onClose }) {
  const pC = checkins[project?.id] || []
  const { streak, todayDone } = streakOf(pC)
  const [sel,  setSel]  = useState([])
  const [note, setNote] = useState('')
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  if (!project) return null
  const actions = project.checkinActions?.length ? project.checkinActions : CI_TPL.slice(0,6)
  const toggle  = a => setSel(p => p.includes(a) ? p.filter(x=>x!==a) : [...p,a])

  const submit = async () => {
    setBusy(true)
    await onCheckin(project.id, {
      id: uid(), date: TODAY,
      time: new Date().toLocaleTimeString('id-ID', { hour:'2-digit', minute:'2-digit' }),
      actions: sel.length ? sel : ['Daily Check'],
      note: note.trim(),
    })
    setBusy(false)
    setDone(true)
  }

  return (
    <Modal open={!!project} onClose={onClose} title={`Check-in — ${project.name}`}>
      <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
        <div style={{ display:'flex', gap:'10px', background:'rgba(255,193,7,0.06)',
          border:'1px solid rgba(255,193,7,0.2)', borderRadius:'12px', padding:'12px', alignItems:'center' }}>
          <div style={{ fontSize:'28px' }}>🔥</div>
          <div style={{ flex:1 }}>
            <div style={{ color:'#ffc107', fontFamily:"'Space Mono',monospace", fontWeight:'700', fontSize:'18px', lineHeight:1 }}>{streak} Day Streak</div>
            <div style={{ color:'#8892b0', fontSize:'11px', marginTop:'3px' }}>{streak===0?'Mulai streak hari ini!':`${streak} hari berturut-turut!`}</div>
          </div>
          <Heatmap list={pC} days={7} />
        </div>
        {todayDone && !done && (
          <div style={{ background:'rgba(0,255,157,0.08)', border:'1px solid rgba(0,255,157,0.2)',
            borderRadius:'10px', padding:'10px 14px', color:'#00ff9d', fontSize:'12px' }}>
            ✅ Sudah check-in hari ini
          </div>
        )}
        {done ? (
          <div style={{ background:'rgba(0,255,157,0.12)', border:'1px solid rgba(0,255,157,0.3)',
            borderRadius:'10px', padding:'20px', textAlign:'center' }}>
            <div style={{ fontSize:'36px', marginBottom:'8px' }}>✅</div>
            <div style={{ color:'#00ff9d', fontFamily:"'Space Mono',monospace", fontWeight:'700', fontSize:'16px' }}>Check-in Berhasil!</div>
            <div style={{ color:'#8892b0', fontSize:'12px', marginTop:'5px' }}>Data tersimpan ke cloud ☁️</div>
            <button onClick={onClose} style={{ ...BTN('rgba(0,255,157,0.15)','#00ff9d60','#00ff9d'), marginTop:'14px' }}>Tutup</button>
          </div>
        ) : (
          <>
            <div>
              <Lbl>Aksi hari ini</Lbl>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'7px', marginTop:'8px' }}>
                {actions.map(a => (
                  <button key={a} onClick={() => toggle(a)} style={{
                    background: sel.includes(a)?'rgba(0,255,157,0.18)':'#141920',
                    border: sel.includes(a)?'1px solid #00ff9d80':'1px solid #1e2535',
                    borderRadius:'8px', color: sel.includes(a)?'#00ff9d':'#8892b0',
                    padding:'8px 12px', cursor:'pointer', fontSize:'12px', fontFamily:"'Space Mono',monospace",
                  }}>{sel.includes(a)?'✓ ':''}{a}</button>
                ))}
              </div>
            </div>
            <div>
              <Lbl>Catatan (opsional)</Lbl>
              <textarea style={{ ...INP, marginTop:'6px', minHeight:'72px', resize:'vertical' }}
                placeholder="Progress, kendala, atau catatan lain..."
                value={note} onChange={e=>setNote(e.target.value)} />
            </div>
            <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
              <button onClick={onClose} style={BTN('none','#1e2535','#8892b0')}>Batal</button>
              <button onClick={submit} disabled={busy} style={{ ...BTN('rgba(0,255,157,0.18)','#00ff9d70','#00ff9d'), padding:'10px 20px', fontSize:'13px', opacity:busy?0.7:1 }}>
                {busy?'Menyimpan...':'🔔 Check-in'}
              </button>
            </div>
          </>
        )}
        {pC.length > 0 && !done && (
          <div>
            <Lbl>Riwayat (5 terakhir)</Lbl>
            <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginTop:'8px' }}>
              {[...pC].sort((a,b)=>b.date.localeCompare(a.date)).slice(0,5).map(c=>(
                <div key={c.id} style={{ background:'#141920', borderRadius:'8px', padding:'9px 12px', display:'flex', gap:'10px' }}>
                  <div style={{ minWidth:'80px' }}>
                    <div style={{ color:c.date===TODAY?'#00ff9d':'#8892b0', fontSize:'11px', fontFamily:"'Space Mono',monospace" }}>{c.date}</div>
                    <div style={{ color:'#555', fontSize:'10px' }}>{c.time}</div>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', marginBottom:c.note?'4px':'0' }}>
                      {c.actions?.map(a=><Tag key={a} c='#4fc3f7'>{a}</Tag>)}
                    </div>
                    {c.note && <div style={{ color:'#8892b0', fontSize:'11px' }}>{c.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function ProjectForm({ initial, onSave, onClose, saving }) {
  const [f, sf] = useState({ ...EMPTY, ...initial })
  const [ti, sti] = useState('')
  const [ci, sci] = useState('')
  const set = (k,v) => sf(p=>({...p,[k]:v}))
  const addTask = n => { const t=(n||ti).trim(); if(!t)return; sf(p=>({...p,tasks:[...p.tasks,{id:uid(),name:t,done:false}]})); sti('') }
  const toggleTask = id => sf(p=>({...p,tasks:p.tasks.map(t=>t.id===id?{...t,done:!t.done}:t)}))
  const delTask    = id => sf(p=>({...p,tasks:p.tasks.filter(t=>t.id!==id)}))
  const addCI = n => { const t=(n||ci).trim(); if(!t||f.checkinActions.includes(t))return; sf(p=>({...p,checkinActions:[...p.checkinActions,t]})); sci('') }
  const delCI = n => sf(p=>({...p,checkinActions:p.checkinActions.filter(x=>x!==n)}))

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
        <F label="Nama Project" span2><input style={INP} value={f.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. zkSync Airdrop" /></F>
        <F label="Blockchain"><select style={INP} value={f.chain} onChange={e=>set('chain',e.target.value)}>{CHAINS.map(c=><option key={c}>{c}</option>)}</select></F>
        <F label="Kategori"><select style={INP} value={f.category} onChange={e=>set('category',e.target.value)}>{CATS.map(c=><option key={c}>{c}</option>)}</select></F>
        <F label="Status"><select style={INP} value={f.status} onChange={e=>set('status',e.target.value)}>{Object.entries(STATUS_CFG).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}</select></F>
        <F label="Priority"><select style={INP} value={f.priority} onChange={e=>set('priority',e.target.value)}>{PRIOS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}</select></F>
        <F label="Est. Reward ($)"><input style={INP} type="number" value={f.estimatedReward} onChange={e=>set('estimatedReward',e.target.value)} placeholder="500"/></F>
        <F label="Modal ($)"><input style={INP} type="number" value={f.investedAmount} onChange={e=>set('investedAmount',e.target.value)} placeholder="20"/></F>
        <F label="Wallet" span2><input style={INP} value={f.walletUsed} onChange={e=>set('walletUsed',e.target.value)} placeholder="0x..."/></F>
        <F label="Mulai"><input style={INP} type="date" value={f.startDate} onChange={e=>set('startDate',e.target.value)}/></F>
        <F label="Deadline"><input style={INP} type="date" value={f.deadline} onChange={e=>set('deadline',e.target.value)}/></F>
        <F label="Website" span2><input style={INP} value={f.website} onChange={e=>set('website',e.target.value)} placeholder="https://..."/></F>
        <F label="Twitter"><input style={INP} value={f.twitter} onChange={e=>set('twitter',e.target.value)} placeholder="@handle"/></F>
        <F label="Discord"><input style={INP} value={f.discord} onChange={e=>set('discord',e.target.value)} placeholder="invite link"/></F>
        <F label="Funding"><input style={INP} value={f.fundingAmount} onChange={e=>set('fundingAmount',e.target.value)} placeholder="$50M Series A"/></F>
        <F label="Backers"><input style={INP} value={f.backers} onChange={e=>set('backers',e.target.value)} placeholder="a16z, Binance Labs"/></F>
        <F label="Tags (pisah koma)" span2><input style={INP} value={f.tags} onChange={e=>set('tags',e.target.value)} placeholder="testnet, mainnet"/></F>
        <F label="Catatan" span2><textarea style={{...INP,minHeight:'65px',resize:'vertical'}} value={f.notes} onChange={e=>set('notes',e.target.value)} placeholder="Strategi, info penting..."/></F>
      </div>

      <div style={{ background:'#0a0d14', borderRadius:'12px', padding:'14px', border:'1px solid #1e2535' }}>
        <Lbl>📋 Checklist Tasks</Lbl>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'8px', marginBottom:'8px' }}>
          {TASK_TPL.map(t=><button key={t} onClick={()=>addTask(t)} style={{ background:'#141920', border:'1px solid #1e2535', borderRadius:'6px', color:'#8892b0', padding:'5px 9px', fontSize:'11px', cursor:'pointer' }}>+{t}</button>)}
        </div>
        <div style={{ display:'flex', gap:'7px' }}>
          <input style={{...INP,flex:1}} value={ti} onChange={e=>sti(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addTask()} placeholder="Custom task..."/>
          <button onClick={()=>addTask()} style={{ ...BTN('rgba(0,255,157,0.12)','#00ff9d40','#00ff9d'), padding:'0 14px' }}>+</button>
        </div>
        {f.tasks.length>0 && (
          <div style={{ marginTop:'8px', display:'flex', flexDirection:'column', gap:'5px' }}>
            {f.tasks.map(t=>(
              <div key={t.id} style={{ display:'flex', alignItems:'center', gap:'9px', background:'#141920', borderRadius:'7px', padding:'8px 11px', border:'1px solid #1e2535' }}>
                <input type="checkbox" checked={t.done} onChange={()=>toggleTask(t.id)} style={{ accentColor:'#00ff9d', width:'16px', height:'16px' }}/>
                <span style={{ flex:1, color:t.done?'#555':'#c0caf5', textDecoration:t.done?'line-through':'none', fontSize:'13px' }}>{t.name}</span>
                <button onClick={()=>delTask(t.id)} style={{ background:'none', border:'none', color:'#555', cursor:'pointer', fontSize:'18px', padding:'0 4px' }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ background:'#0a0d14', borderRadius:'12px', padding:'14px', border:'1px solid #1e2535' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:f.requiresDailyCheckin?'14px':'0' }}>
          <div>
            <div style={{ color:'#e2e8f0', fontSize:'13px', fontWeight:'600' }}>🔔 Daily Check-in</div>
            <div style={{ color:'#8892b0', fontSize:'11px', marginTop:'2px' }}>Perlu dikerjakan setiap hari</div>
          </div>
          <div onClick={()=>set('requiresDailyCheckin',!f.requiresDailyCheckin)} style={{
            width:'48px', height:'26px', borderRadius:'13px', cursor:'pointer', position:'relative',
            background:f.requiresDailyCheckin?'rgba(0,255,157,0.4)':'#1e2535',
            border:f.requiresDailyCheckin?'1px solid #00ff9d':'1px solid #333',
            transition:'background 0.2s', flexShrink:0,
          }}>
            <div style={{ position:'absolute', top:'4px', left:f.requiresDailyCheckin?'24px':'4px',
              width:'16px', height:'16px', borderRadius:'50%',
              background:f.requiresDailyCheckin?'#00ff9d':'#555', transition:'left 0.2s' }}/>
          </div>
        </div>
        {f.requiresDailyCheckin && (
          <div>
            <Lbl>Aksi check-in harian</Lbl>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'8px', marginBottom:'8px' }}>
              {CI_TPL.map(a=><button key={a} onClick={()=>addCI(a)} disabled={f.checkinActions.includes(a)} style={{ background:f.checkinActions.includes(a)?'rgba(0,255,157,0.12)':'#141920', border:f.checkinActions.includes(a)?'1px solid #00ff9d40':'1px solid #1e2535', borderRadius:'6px', color:f.checkinActions.includes(a)?'#00ff9d':'#8892b0', padding:'5px 9px', fontSize:'11px', cursor:'pointer' }}>+{a}</button>)}
            </div>
            <div style={{ display:'flex', gap:'7px' }}>
              <input style={{...INP,flex:1}} value={ci} onChange={e=>sci(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addCI()} placeholder="Custom aksi..."/>
              <button onClick={()=>addCI()} style={{ ...BTN('rgba(0,255,157,0.12)','#00ff9d40','#00ff9d'), padding:'0 14px' }}>+</button>
            </div>
            {f.checkinActions.length>0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'8px' }}>
                {f.checkinActions.map(a=>(
                  <span key={a} style={{ background:'rgba(0,255,157,0.1)', border:'1px solid #00ff9d40', borderRadius:'6px', color:'#00ff9d', padding:'4px 9px', fontSize:'11px', display:'flex', alignItems:'center', gap:'5px' }}>
                    {a}<span onClick={()=>delCI(a)} style={{ cursor:'pointer', color:'#555', fontWeight:'700', fontSize:'14px' }}>×</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
        <button onClick={onClose} style={BTN('none','#1e2535','#8892b0')}>Batal</button>
        <button onClick={()=>{ if(!f.name.trim())return alert('Nama project wajib diisi!'); onSave(f) }}
          disabled={saving}
          style={{ ...BTN('rgba(0,255,157,0.15)','#00ff9d70','#00ff9d'), padding:'10px 20px', fontSize:'13px', opacity:saving?0.7:1 }}>
          {saving ? '💾 Menyimpan...' : '💾 Simpan'}
        </button>
      </div>
    </div>
  )
}

function DetailModal({ project:p, checkins, onClose, onEdit, onCheckin }) {
  if (!p) return null
  const st  = STATUS_CFG[p.status] || STATUS_CFG.active
  const doneT = p.tasks.filter(t=>t.done).length
  const totT  = p.tasks.length
  const pct   = totT>0?Math.round((doneT/totT)*100):0
  const { streak, todayDone, total:ciTotal } = streakOf(checkins)

  return (
    <Modal open={!!p} onClose={onClose} title={p.name} wide>
      <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'7px' }}>
          <span style={{ background:st.bg, color:st.color, border:`1px solid ${st.color}40`, borderRadius:'7px', padding:'4px 11px', fontSize:'11px', fontFamily:"'Space Mono',monospace" }}>{st.label}</span>
          <Tag c='#8892b0'>{p.chain}</Tag><Tag c='#a78bfa'>{p.category}</Tag>
          {p.fundingAmount&&<Tag c='#ffc107'>💰{p.fundingAmount}</Tag>}
          {p.backers&&<Tag c='#4fc3f7'>🏦{p.backers}</Tag>}
        </div>

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px' }}>
          {[
            { label:'EST. REWARD', val:p.estimatedReward>0?`$${Number(p.estimatedReward).toLocaleString()}`:'—', c:'#00ff9d' },
            { label:'MODAL',       val:p.investedAmount>0?`$${Number(p.investedAmount).toLocaleString()}`:'—',  c:'#ffc107' },
            { label:'ROI',         val:p.estimatedReward>0&&p.investedAmount>0?`${Math.round((p.estimatedReward/p.investedAmount)*100)}%`:'—', c:'#4fc3f7' },
          ].map(s=>(
            <div key={s.label} style={{ background:'#141920', borderRadius:'10px', padding:'12px', textAlign:'center', border:'1px solid #1e2535' }}>
              <div style={{ color:'#555', fontSize:'9px', fontFamily:"'Space Mono',monospace", marginBottom:'4px' }}>{s.label}</div>
              <div style={{ color:s.c, fontWeight:'800', fontSize:'18px', fontFamily:"'Space Mono',monospace" }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Daily Check-in */}
        {p.requiresDailyCheckin && (
          <div style={{ background:'rgba(255,193,7,0.05)', border:'1px solid rgba(255,193,7,0.2)', borderRadius:'12px', padding:'14px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }}>
              <div>
                <div style={{ color:'#ffc107', fontFamily:"'Space Mono',monospace", fontWeight:'700', fontSize:'15px' }}>🔥 {streak} Day Streak</div>
                <div style={{ color:'#8892b0', fontSize:'11px', marginTop:'2px' }}>Total {ciTotal} check-in · {todayDone?'Sudah check-in hari ini ✅':'Belum check-in hari ini ⚠️'}</div>
              </div>
              <Heatmap list={checkins} days={14}/>
            </div>
            <button onClick={()=>{ onClose(); onCheckin(p) }} style={{ ...BTN(todayDone?'rgba(0,255,157,0.06)':'rgba(0,255,157,0.2)','#00ff9d60','#00ff9d'), marginTop:'12px', padding:'9px 20px' }}>
              {todayDone?'✅ Lihat / Check-in Lagi':'🔔 Check-in Sekarang'}
            </button>
          </div>
        )}

        {/* Wallet */}
        {p.walletUsed && (
          <div>
            <Lbl>Wallet</Lbl>
            <div style={{ background:'#141920', borderRadius:'8px', padding:'9px 12px', color:'#8892b0', fontSize:'12px', fontFamily:"'Space Mono',monospace", wordBreak:'break-all', marginTop:'5px' }}>{p.walletUsed}</div>
          </div>
        )}

        {/* Links */}
        {(p.website||p.twitter||p.discord) && (
          <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', background:'#141920', borderRadius:'10px', padding:'12px 14px', border:'1px solid #1e2535' }}>
            {p.website&&<a href={p.website} target="_blank" rel="noreferrer" style={{ color:'#4fc3f7', fontSize:'13px', display:'flex', alignItems:'center', gap:'5px' }}>🌐 Website</a>}
            {p.twitter&&<a href={`https://twitter.com/${p.twitter.replace('@','')}`} target="_blank" rel="noreferrer" style={{ color:'#4fc3f7', fontSize:'13px', display:'flex', alignItems:'center', gap:'5px' }}>🐦 Twitter</a>}
            {p.discord&&<a href={p.discord} target="_blank" rel="noreferrer" style={{ color:'#4fc3f7', fontSize:'13px', display:'flex', alignItems:'center', gap:'5px' }}>💬 Discord</a>}
          </div>
        )}

        {/* Tasks */}
        {totT>0 && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}><Lbl>Tasks — {pct}%</Lbl><span style={{ color:'#8892b0', fontSize:'11px', fontFamily:"'Space Mono',monospace" }}>{doneT}/{totT}</span></div>
            <div style={{ background:'#1a2030', borderRadius:'5px', height:'6px', overflow:'hidden', marginBottom:'10px' }}><div style={{ width:`${pct}%`, height:'100%', background:'linear-gradient(90deg,#00ff9d,#4fc3f7)' }}/></div>
            <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
              {p.tasks.map(t=>(
                <div key={t.id} style={{ display:'flex', alignItems:'center', gap:'9px', background:'#141920', borderRadius:'7px', padding:'8px 11px' }}>
                  <span style={{ color:t.done?'#00ff9d':'#333', fontSize:'14px' }}>{t.done?'✓':'○'}</span>
                  <span style={{ color:t.done?'#555':'#c0caf5', textDecoration:t.done?'line-through':'none', fontSize:'12px' }}>{t.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {p.notes && (
          <div>
            <Lbl>Catatan</Lbl>
            <div style={{ background:'#141920', borderRadius:'8px', padding:'11px 13px', color:'#8892b0', fontSize:'12px', lineHeight:'1.6', whiteSpace:'pre-wrap', marginTop:'5px' }}>{p.notes}</div>
          </div>
        )}

        <div style={{ display:'flex', gap:'9px', justifyContent:'flex-end' }}>
          <button onClick={onClose} style={BTN('none','#1e2535','#8892b0')}>Tutup</button>
          <button onClick={()=>{ onClose(); onEdit(p) }} style={BTN('rgba(167,139,250,0.12)','#a78bfa60','#a78bfa')}>Edit Project</button>
        </div>
      </div>
    </Modal>
  )
}

function ProjectCard({ project:p, checkins, onEdit, onDelete, onView, onCheckin }) {
  const st  = STATUS_CFG[p.status] || STATUS_CFG.active
  const pr  = PRIOS.find(x=>x.value===p.priority)
  const doneT = p.tasks.filter(t=>t.done).length
  const totT  = p.tasks.length
  const pct   = totT>0?Math.round((doneT/totT)*100):0
  const tags  = p.tags?p.tags.split(',').map(t=>t.trim()).filter(Boolean):[]
  const { streak, todayDone } = streakOf(checkins)

  return (
    <div style={{ background:'#0e1117', border:'1px solid #1e2535', borderRadius:'14px',
      padding:'16px', cursor:'pointer', transition:'border-color 0.2s',
      position:'relative', overflow:'hidden', WebkitTapHighlightColor:'transparent' }}
      onClick={()=>onView(p)}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:'3px', background:pr?.color||'#ffc107', opacity:0.75 }}/>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
        <div style={{ flex:1, marginRight:'8px' }}>
          <div style={{ color:'#e2e8f0', fontWeight:'700', fontSize:'14px', fontFamily:"'Space Mono',monospace", marginBottom:'5px', lineHeight:1.3 }}>{p.name}</div>
          <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}><Tag c='#8892b0'>{p.chain}</Tag><Tag c='#a78bfa'>{p.category}</Tag></div>
        </div>
        <span style={{ background:st.bg, color:st.color, border:`1px solid ${st.color}40`, borderRadius:'8px', padding:'4px 9px', fontSize:'10px', fontFamily:"'Space Mono',monospace", whiteSpace:'nowrap', flexShrink:0 }}>
          <span style={{ display:'inline-block', width:'5px', height:'5px', borderRadius:'50%', background:st.dot, marginRight:'4px' }}/>{st.label}
        </span>
      </div>

      <div style={{ display:'flex', gap:'14px', marginBottom:'12px', flexWrap:'wrap' }}>
        {p.estimatedReward>0 && <div><div style={{ color:'#555', fontSize:'9px', fontFamily:"'Space Mono',monospace" }}>EST. REWARD</div><div style={{ color:'#00ff9d', fontWeight:'700', fontSize:'16px' }}>${Number(p.estimatedReward).toLocaleString()}</div></div>}
        {p.investedAmount>0  && <div><div style={{ color:'#555', fontSize:'9px', fontFamily:"'Space Mono',monospace" }}>MODAL</div><div style={{ color:'#ffc107', fontWeight:'700', fontSize:'16px' }}>${Number(p.investedAmount).toLocaleString()}</div></div>}
        {p.requiresDailyCheckin && <div style={{ marginLeft:'auto', textAlign:'right' }}><div style={{ color:'#555', fontSize:'9px', fontFamily:"'Space Mono',monospace" }}>STREAK</div><div style={{ color:'#ffc107', fontWeight:'700', fontSize:'16px' }}>🔥{streak}</div></div>}
      </div>

      {totT>0 && (
        <div style={{ marginBottom:'10px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
            <span style={{ color:'#555', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>TASKS</span>
            <span style={{ color:'#8892b0', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>{doneT}/{totT}</span>
          </div>
          <div style={{ background:'#1a2030', borderRadius:'4px', height:'6px', overflow:'hidden' }}>
            <div style={{ width:`${pct}%`, height:'100%', background:pct===100?'#00ff9d':'linear-gradient(90deg,#00ff9d,#4fc3f7)', borderRadius:'4px' }}/>
          </div>
        </div>
      )}

      {tags.length>0 && <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', marginBottom:'10px' }}>{tags.map(t=><Tag key={t} c='#555'>#{t}</Tag>)}</div>}
      {p.requiresDailyCheckin && <div style={{ marginBottom:'10px' }}><Heatmap list={checkins} days={7}/></div>}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'6px' }}>
        <div>{p.deadline&&<span style={{ color:'#555', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>📅{p.deadline}</span>}</div>
        <div style={{ display:'flex', gap:'6px' }} onClick={e=>e.stopPropagation()}>
          {p.requiresDailyCheckin && (
            <button onClick={()=>onCheckin(p)} style={{ background:todayDone?'rgba(0,255,157,0.06)':'rgba(0,255,157,0.18)', border:todayDone?'1px solid rgba(0,255,157,0.15)':'1px solid #00ff9d60', borderRadius:'6px', color:'#00ff9d', padding:'7px 12px', cursor:'pointer', fontSize:'12px', fontFamily:"'Space Mono',monospace", fontWeight:'700' }}>{todayDone?'✓':'🔔'}</button>
          )}
          <button onClick={()=>onEdit(p)} style={{ ...BTN('rgba(167,139,250,0.08)','#a78bfa40','#a78bfa'), padding:'7px 12px' }}>Edit</button>
          <button onClick={()=>onDelete(p.id)} style={{ ...BTN('rgba(255,77,109,0.08)','#ff4d6d40','#ff4d6d'), padding:'7px 12px' }}>Del</button>
        </div>
      </div>
    </div>
  )
}

export default function App() {
  const { user, loading: authLoading, signOut } = useAuth()
  const { projects, checkins, loading, syncing, error, saveProject, deleteProject, addCheckin } = useProjects(user?.id)

  const [tab,      setTab]      = useState('projects')
  const [showForm, setShowForm] = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [editProj, setEditProj] = useState(null)
  const [viewProj, setViewProj] = useState(null)
  const [ciProj,   setCiProj]   = useState(null)
  const [search,   setSearch]   = useState('')
  const [fStatus,  setFStatus]  = useState('all')
  const [fChain,   setFChain]   = useState('all')
  const [fPri,     setFPri]     = useState('all')
  const [sortBy,   setSortBy]   = useState('newest')
  const [showFilter, setShowFilter] = useState(false)

  if (authLoading) return (
    <div style={{ minHeight:'100vh', background:'#080b10', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'#00ff9d', fontFamily:"'Space Mono',monospace", fontSize:'14px' }}>⚡ Loading...</div>
    </div>
  )
  if (!user) return <AuthPage />

  const handleSave = async (f) => {
    setSaving(true)
    try { await saveProject(f); setShowForm(false); setEditProj(null) }
    catch (e) { alert('Gagal menyimpan: ' + e.message) }
    finally { setSaving(false) }
  }
  const handleDelete = async (id) => {
    if (!window.confirm('Hapus project ini?')) return
    try { await deleteProject(id) } catch (e) { alert('Gagal hapus: ' + e.message) }
  }
  const handleCheckin = async (pid, entry) => { await addCheckin(pid, entry) }

  let filtered = projects.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.chain.toLowerCase().includes(search.toLowerCase())) return false
    if (fStatus!=='all' && p.status!==fStatus) return false
    if (fChain!=='all'  && p.chain!==fChain)   return false
    if (fPri!=='all'    && p.priority!==fPri)  return false
    return true
  })
  if (sortBy==='reward')   filtered.sort((a,b)=>(Number(b.estimatedReward)||0)-(Number(a.estimatedReward)||0))
  else if (sortBy==='priority') { const o={high:0,medium:1,low:2}; filtered.sort((a,b)=>(o[a.priority]||1)-(o[b.priority]||1)) }
  else if (sortBy==='streak')   filtered.sort((a,b)=>streakOf(checkins[b.id]||[]).streak-streakOf(checkins[a.id]||[]).streak)
  else filtered.sort((a,b)=>(b.createdAt||'').localeCompare(a.createdAt||''))

  const totalEst = projects.reduce((s,p)=>s+(Number(p.estimatedReward)||0),0)
  const totalInv = projects.reduce((s,p)=>s+(Number(p.investedAmount)||0),0)
  const actCount = projects.filter(p=>p.status==='active').length
  const ciProjs  = projects.filter(p=>p.requiresDailyCheckin)
  const ciToday  = ciProjs.filter(p=>streakOf(checkins[p.id]||[]).todayDone).length

  const allHistory = Object.entries(checkins).flatMap(([pid,list])=>{
    const proj = projects.find(p=>p.id===pid)
    return (list||[]).map(c=>({ ...c, projectName:proj?.name||'Unknown' }))
  }).sort((a,b)=>b.date.localeCompare(a.date)).slice(0,80)

  const TABS = [
    { id:'projects', label:'📁', fullLabel:'Projects' },
    { id:'checkin',  label:'🔔', fullLabel:`Check-in${ciProjs.length>0?` ${ciToday}/${ciProjs.length}`:''}` },
    { id:'history',  label:'📜', fullLabel:'Riwayat' },
  ]

  return (
    <div style={{ minHeight:'100vh', background:'#080b10', fontFamily:"'Inter',sans-serif", color:'#c0caf5' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Inter:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:#0e1117}::-webkit-scrollbar-thumb{background:#1e2535;border-radius:3px}
        input:focus,select:focus,textarea:focus{border-color:#00ff9d60!important}
        a{text-decoration:none}
        @media(max-width:600px){
          .stats-grid{grid-template-columns:repeat(2,1fr)!important}
          .project-grid{grid-template-columns:1fr!important}
          .filter-row{flex-direction:column!important}
          .nav-email{display:none!important}
          .desktop-tabs{display:none!important}
        }
        @media(min-width:601px){
          .mobile-bottom-nav{display:none!important}
          .mobile-fab{display:none!important}
        }
      `}</style>

      {/* TOP NAV */}
      <div style={{ borderBottom:'1px solid #1e2535', padding:'0 16px', background:'rgba(8,11,16,0.97)',
        backdropFilter:'blur(10px)', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ maxWidth:'1280px', margin:'0 auto', display:'flex',
          alignItems:'center', justifyContent:'space-between', height:'52px', gap:'10px' }}>

          {/* Logo */}
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
            <div style={{ width:'28px', height:'28px', borderRadius:'7px', background:'rgba(0,255,157,0.12)',
              border:'1px solid #00ff9d40', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px' }}>⚡</div>
            <div>
              <div style={{ color:'#00ff9d', fontFamily:"'Space Mono',monospace", fontWeight:'700', fontSize:'12px', lineHeight:1 }}>LEVIARCH</div>
              <div style={{ color:'#555', fontSize:'8px', fontFamily:"'Space Mono',monospace" }}>AIRDROP TRACKER</div>
            </div>
          </div>

          {/* Desktop Tabs */}
          <div className="desktop-tabs" style={{ display:'flex', gap:'4px', flex:1, justifyContent:'center' }}>
            {TABS.map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)} style={{
                background:tab===t.id?'rgba(0,255,157,0.1)':'none',
                border:tab===t.id?'1px solid #00ff9d40':'1px solid transparent',
                borderRadius:'8px', color:tab===t.id?'#00ff9d':'#8892b0',
                padding:'6px 14px', cursor:'pointer', fontSize:'11px',
                fontFamily:"'Space Mono',monospace", whiteSpace:'nowrap',
              }}>{t.label} {t.fullLabel}</button>
            ))}
          </div>

          {/* Right side */}
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
            <SyncBadge syncing={syncing} error={error} />
            <span className="nav-email" style={{ color:'#555', fontSize:'10px', maxWidth:'120px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.email}</span>
            <button onClick={()=>{ setEditProj(null); setShowForm(true) }} style={{
              background:'rgba(0,255,157,0.14)', border:'1px solid #00ff9d60',
              borderRadius:'9px', color:'#00ff9d', padding:'7px 14px',
              cursor:'pointer', fontSize:'12px', fontFamily:"'Space Mono',monospace", fontWeight:'700',
            }}>+ NEW</button>
            <button onClick={signOut} style={{ ...BTN('rgba(255,77,109,0.08)','#ff4d6d40','#ff4d6d'), padding:'7px 10px', fontSize:'11px' }}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'16px 16px 80px' }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:'80px 0', color:'#555' }}>
            <div style={{ fontSize:'32px', marginBottom:'12px' }}>⟳</div>
            <div style={{ fontFamily:"'Space Mono',monospace", fontSize:'13px' }}>Memuat data...</div>
          </div>
        ) : (
          <>
            {/* STATS — 2 kolom di mobile, 5 di desktop */}
            <div className="stats-grid" style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'8px', marginBottom:'16px' }}>
              {[
                { label:'PROJECTS',   val:projects.length,                c:'#c0caf5', icon:'📁' },
                { label:'ACTIVE',     val:actCount,                       c:'#00ff9d', icon:'🔥' },
                { label:'CHECK-IN',   val:`${ciToday}/${ciProjs.length}`, c:ciToday===ciProjs.length&&ciProjs.length>0?'#00ff9d':'#ffc107', icon:'🔔' },
                { label:'PORTFOLIO',  val:`$${totalEst.toLocaleString()}`, c:'#00ff9d', icon:'💎' },
                { label:'MODAL',      val:`$${totalInv.toLocaleString()}`, c:'#ffc107', icon:'💸' },
              ].map(s=>(
                <div key={s.label} style={{ background:'#0e1117', border:'1px solid #1e2535', borderRadius:'10px', padding:'10px 12px' }}>
                  <div style={{ color:'#555', fontSize:'8px', fontFamily:"'Space Mono',monospace", marginBottom:'3px' }}>{s.icon} {s.label}</div>
                  <div style={{ color:s.c, fontFamily:"'Space Mono',monospace", fontWeight:'700', fontSize:'15px' }}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* TAB: PROJECTS */}
            {tab==='projects' && (
              <>
                {/* Search + Filter Toggle */}
                <div style={{ display:'flex', gap:'8px', marginBottom:'10px' }}>
                  <input style={{...INP, flex:1}} placeholder="🔍 Cari project..." value={search} onChange={e=>setSearch(e.target.value)}/>
                  <button onClick={()=>setShowFilter(!showFilter)} style={{ ...BTN(showFilter?'rgba(0,255,157,0.12)':'#141920', showFilter?'#00ff9d40':'#1e2535', showFilter?'#00ff9d':'#8892b0'), padding:'0 14px', whiteSpace:'nowrap' }}>
                    ⚙ Filter
                  </button>
                </div>

                {/* Filter Panel — collapsible di mobile */}
                {showFilter && (
                  <div style={{ display:'flex', gap:'7px', flexWrap:'wrap', marginBottom:'10px', background:'#0e1117', padding:'12px', borderRadius:'10px', border:'1px solid #1e2535' }}>
                    <select style={{...INP, flex:'1 1 120px'}} value={fStatus} onChange={e=>setFStatus(e.target.value)}>
                      <option value="all">Semua Status</option>
                      {Object.entries(STATUS_CFG).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <select style={{...INP, flex:'1 1 120px'}} value={fChain} onChange={e=>setFChain(e.target.value)}>
                      <option value="all">Semua Chain</option>
                      {CHAINS.map(c=><option key={c}>{c}</option>)}
                    </select>
                    <select style={{...INP, flex:'1 1 120px'}} value={fPri} onChange={e=>setFPri(e.target.value)}>
                      <option value="all">Semua Priority</option>
                      {PRIOS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <select style={{...INP, flex:'1 1 120px'}} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                      <option value="newest">Terbaru</option>
                      <option value="reward">Reward Terbesar</option>
                      <option value="priority">Priority</option>
                      <option value="streak">Streak Terpanjang</option>
                    </select>
                  </div>
                )}

                <div style={{ color:'#555', fontSize:'11px', fontFamily:"'Space Mono',monospace", marginBottom:'10px' }}>{filtered.length} project</div>

                {filtered.length===0 ? (
                  <div style={{ textAlign:'center', padding:'60px 0', color:'#555' }}>
                    <div style={{ fontSize:'40px', marginBottom:'12px' }}>🎯</div>
                    <div style={{ fontFamily:"'Space Mono',monospace", fontSize:'13px', marginBottom:'6px' }}>Belum ada project</div>
                    <button onClick={()=>{ setEditProj(null); setShowForm(true) }} style={{ marginTop:'12px', ...BTN('rgba(0,255,157,0.14)','#00ff9d60','#00ff9d'), padding:'10px 20px', fontSize:'13px' }}>+ Tambah Project</button>
                  </div>
                ) : (
                  <div className="project-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:'12px' }}>
                    {filtered.map(p=>(
                      <ProjectCard key={p.id} project={p} checkins={checkins[p.id]||[]}
                        onEdit={p=>{ setEditProj(p); setShowForm(true) }}
                        onDelete={handleDelete} onView={setViewProj} onCheckin={setCiProj}/>
                    ))}
                  </div>
                )}

                {projects.length>0 && (
                  <div style={{ marginTop:'16px', padding:'12px 14px', background:'#0e1117', border:'1px solid #1e2535', borderRadius:'10px', display:'flex', gap:'20px', flexWrap:'wrap' }}>
                    <div><span style={{ color:'#555', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>MODAL </span><span style={{ color:'#ffc107', fontWeight:'700', fontFamily:"'Space Mono',monospace" }}>${totalInv.toLocaleString()}</span></div>
                    <div><span style={{ color:'#555', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>PORTFOLIO </span><span style={{ color:'#00ff9d', fontWeight:'700', fontFamily:"'Space Mono',monospace" }}>${totalEst.toLocaleString()}</span></div>
                    {totalInv>0&&<div><span style={{ color:'#555', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>ROI </span><span style={{ color:'#4fc3f7', fontWeight:'700', fontFamily:"'Space Mono',monospace" }}>{Math.round((totalEst/totalInv)*100)}%</span></div>}
                  </div>
                )}
              </>
            )}

            {/* TAB: CHECK-IN HUB */}
            {tab==='checkin' && (
              <div>
                <div style={{ marginBottom:'14px' }}>
                  <div style={{ color:'#e2e8f0', fontFamily:"'Space Mono',monospace", fontSize:'14px', fontWeight:'700', marginBottom:'3px' }}>🔔 Daily Check-in Hub</div>
                  <div style={{ color:'#8892b0', fontSize:'12px' }}>Hari ini: {ciToday}/{ciProjs.length} selesai</div>
                </div>
                {ciProjs.length>0 && (
                  <div style={{ marginBottom:'14px', background:'#0e1117', borderRadius:'10px', padding:'12px 14px', border:'1px solid #1e2535' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
                      <span style={{ color:'#555', fontSize:'10px', fontFamily:"'Space Mono',monospace" }}>PROGRESS HARI INI</span>
                      <span style={{ color:'#00ff9d', fontSize:'11px', fontFamily:"'Space Mono',monospace" }}>{ciToday}/{ciProjs.length}</span>
                    </div>
                    <div style={{ background:'#1a2030', borderRadius:'6px', height:'8px', overflow:'hidden' }}>
                      <div style={{ width:`${ciProjs.length>0?Math.round((ciToday/ciProjs.length)*100):0}%`, height:'100%',
                        background:ciToday===ciProjs.length?'#00ff9d':'linear-gradient(90deg,#ffc107,#00ff9d)',
                        borderRadius:'6px', transition:'width 0.5s' }}/>
                    </div>
                  </div>
                )}
                {ciProjs.length===0 ? (
                  <div style={{ textAlign:'center', padding:'60px 0', color:'#555' }}>
                    <div style={{ fontSize:'40px', marginBottom:'12px' }}>🔕</div>
                    <div style={{ fontFamily:"'Space Mono',monospace", fontSize:'13px' }}>Belum ada project dengan daily check-in</div>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'9px' }}>
                    {ciProjs.map(p=>{
                      const pC = checkins[p.id]||[]
                      const { streak, todayDone, lastDate } = streakOf(pC)
                      const pr = PRIOS.find(x=>x.value===p.priority)
                      return (
                        <div key={p.id} style={{ background:'#0e1117', border:`1px solid ${todayDone?'rgba(0,255,157,0.22)':'#1e2535'}`, borderRadius:'13px', padding:'14px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'10px' }}>
                            <div style={{ width:'36px', height:'36px', borderRadius:'50%', flexShrink:0,
                              background:todayDone?'rgba(0,255,157,0.12)':'rgba(255,193,7,0.08)',
                              border:todayDone?'1px solid #00ff9d40':'1px solid #ffc10730',
                              display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px' }}>{todayDone?'✅':'⏳'}</div>
                            <div style={{ flex:1 }}>
                              <div style={{ color:'#e2e8f0', fontWeight:'700', fontSize:'13px', fontFamily:"'Space Mono',monospace", marginBottom:'4px' }}>{p.name}</div>
                              <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                                <Tag c='#8892b0'>{p.chain}</Tag><Tag c='#a78bfa'>{p.category}</Tag>
                                {pr&&<span style={{ color:pr.color, fontSize:'10px' }}>{pr.label}</span>}
                              </div>
                            </div>
                            <div style={{ textAlign:'center' }}>
                              <div style={{ color:'#555', fontSize:'9px', fontFamily:"'Space Mono',monospace" }}>STREAK</div>
                              <div style={{ color:'#ffc107', fontWeight:'700', fontSize:'18px' }}>🔥{streak}</div>
                            </div>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'8px' }}>
                            <div>
                              <Heatmap list={pC} days={14}/>
                              <div style={{ color:'#555', fontSize:'9px', fontFamily:"'Space Mono',monospace", marginTop:'2px' }}>{lastDate?`Terakhir: ${lastDate}`:'Belum pernah check-in'}</div>
                            </div>
                            <button onClick={()=>setCiProj(p)} style={{ ...BTN(todayDone?'rgba(0,255,157,0.06)':'rgba(0,255,157,0.18)', todayDone?'rgba(0,255,157,0.15)':'#00ff9d70', todayDone?'#555':'#00ff9d'), padding:'10px 18px', fontSize:'13px' }}>{todayDone?'✓ Done':'🔔 Check-in'}</button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* TAB: HISTORY */}
            {tab==='history' && (
              <div>
                <div style={{ marginBottom:'14px' }}>
                  <div style={{ color:'#e2e8f0', fontFamily:"'Space Mono',monospace", fontSize:'14px', fontWeight:'700', marginBottom:'3px' }}>📜 Riwayat Check-in</div>
                  <div style={{ color:'#8892b0', fontSize:'12px' }}>Total {allHistory.length} entri</div>
                </div>
                {allHistory.length===0 ? (
                  <div style={{ textAlign:'center', padding:'60px 0', color:'#555' }}>
                    <div style={{ fontSize:'40px', marginBottom:'12px' }}>📭</div>
                    <div style={{ fontFamily:"'Space Mono',monospace", fontSize:'13px' }}>Belum ada riwayat</div>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                    {allHistory.map(e=>(
                      <div key={e.id} style={{ background:'#0e1117', border:'1px solid #1e2535', borderRadius:'11px', padding:'10px 14px', display:'flex', gap:'12px', alignItems:'flex-start' }}>
                        <div style={{ minWidth:'80px' }}>
                          <div style={{ color:e.date===TODAY?'#00ff9d':'#8892b0', fontSize:'11px', fontFamily:"'Space Mono',monospace" }}>{e.date}</div>
                          <div style={{ color:'#555', fontSize:'10px' }}>{e.time}</div>
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ color:'#e2e8f0', fontSize:'13px', fontWeight:'600', marginBottom:'4px' }}>{e.projectName}</div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', marginBottom:e.note?'4px':'0' }}>{e.actions?.map(a=><Tag key={a} c='#4fc3f7'>{a}</Tag>)}</div>
                          {e.note&&<div style={{ color:'#8892b0', fontSize:'11px' }}>{e.note}</div>}
                        </div>
                        {e.date===TODAY&&<div style={{ color:'#00ff9d', fontSize:'10px', fontFamily:"'Space Mono',monospace", alignSelf:'center', flexShrink:0 }}>TODAY</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* MOBILE BOTTOM NAVIGATION */}
      <div className="mobile-bottom-nav" style={{
        position:'fixed', bottom:0, left:0, right:0, zIndex:100,
        background:'rgba(8,11,16,0.97)', borderTop:'1px solid #1e2535',
        backdropFilter:'blur(10px)', display:'flex', padding:'8px 0 12px',
      }}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            flex:1, background:'none', border:'none',
            color: tab===t.id?'#00ff9d':'#555',
            cursor:'pointer', display:'flex', flexDirection:'column',
            alignItems:'center', gap:'3px', padding:'4px 0',
            fontSize:'20px',
          }}>
            <span>{t.label}</span>
            <span style={{ fontSize:'9px', fontFamily:"'Space Mono',monospace", color:tab===t.id?'#00ff9d':'#555' }}>{t.fullLabel}</span>
          </button>
        ))}
      </div>

      {/* MOBILE FAB — tombol + di mobile */}
      <button className="mobile-fab" onClick={()=>{ setEditProj(null); setShowForm(true) }} style={{
        position:'fixed', bottom:'72px', right:'16px', zIndex:200,
        width:'52px', height:'52px', borderRadius:'50%',
        background:'rgba(0,255,157,0.9)', border:'none',
        color:'#080b10', fontSize:'24px', fontWeight:'700',
        cursor:'pointer', boxShadow:'0 4px 20px rgba(0,255,157,0.4)',
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>+</button>

      {/* MODALS */}
      <Modal open={showForm} onClose={()=>{ setShowForm(false); setEditProj(null) }} title={editProj?'Edit Project':'Project Baru'} wide>
        <ProjectForm initial={editProj||{}} onSave={handleSave} onClose={()=>{ setShowForm(false); setEditProj(null) }} saving={saving}/>
      </Modal>

      <DetailModal project={viewProj} checkins={checkins[viewProj?.id]||[]} onClose={()=>setViewProj(null)} onEdit={p=>{ setEditProj(p); setShowForm(true) }} onCheckin={setCiProj}/>

      <CheckinModal project={ciProj} checkins={checkins} onCheckin={handleCheckin} onClose={()=>setCiProj(null)}/>
    </div>
  )
}
